import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import api from "api/api";

// mui components
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormLabel, IconButton, Radio, RadioGroup, Switch, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// components
import { showNotification } from "store/features/notificationSlice";
import Loader from "../common/Loader";
import { consumerFilterPayload } from "utils/consumerFilterUtils";

// redux
import { useDispatch, useSelector } from "react-redux";
import { loadProductSecondaryDataState, loadInterpolationDetailsState, loadInterpolationToggleState } from "store/features/matchNetSlice";

function InterpolationDialog({ variableMapping = [] }) {
    const dispatch = useDispatch();

    // state
    const [disableInterpolationToggle, setDisableInterpolationToggle] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    // redux
    const likertScaleData = useSelector((state) => state.analysisResultsStore.likertScaleData);
    const lookupSelectedProducts = useSelector((state) => state.matchNetStore.lookupSelectedProducts);
    const productSecondaryData = useSelector((state) => state.matchNetStore.productSecondaryData);
    const defaultProductSecondaryData = useSelector((state) => state.matchNetStore.defaultProductSecondaryData);
    const consumerFilters = useSelector((state) => state.matchNetStore.consumerFilters);
    const productColumnVisibility = useSelector((state) => state.productsInfoStore.productsVisibility);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;
    const interpolationToggle = useSelector((state) => state.matchNetStore.interpolationToggle);

    // options
    const [likertScaleOptions, setLikertScaleOptions] = useState([]);
    const [studyTypeMethodOptions, setStudyTypeMethodOptions] = useState([]);
    const [countryOptions, setCountryOptions] = useState([]);

    // values
    const [selectedLikertScale, setSelectedLikertScale] = useState("");
    const [selectedStudyTypeMethod, setSelectedStudyTypeMethod] = useState("");
    const [selectedCountry, setSelectedCountry] = useState("");

    // loader
    const [isLoading, setIsLoading] = useState(false);

    // Handlers
    const handleCloseDialog = () => {
        setIsOpen(false);
        setSelectedLikertScale("");
        setSelectedStudyTypeMethod("");
        setSelectedCountry("");
        dispatch(loadInterpolationToggleState(false));
    };

    const handleInterpolationToggleChange = (event) => {
        if (event.target.checked) {
            setIsOpen(true);
            dispatch(loadInterpolationToggleState(event.target.checked));
        } else {
            dispatch(loadInterpolationToggleState(event.target.checked));
            dispatch(loadProductSecondaryDataState([...defaultProductSecondaryData]));
            setSelectedLikertScale("");
            setSelectedStudyTypeMethod("");
            setSelectedCountry("");
            dispatch(loadInterpolationDetailsState({}));
        }
    };
    // Handle error function
    const handleError = useCallback(
        (type, error) => {
            dispatch(showNotification({ severity: type, data: error }));
        },
        [dispatch]
    );

    useEffect(() => {
        const studyTypeMethods = [];
        const LikertScale = {};
        const countries = [...new Set(lookupSelectedProducts.map((item) => item.country))];

        variableMapping.forEach((item) => {
            if (item.mapping.length > 0) {
                studyTypeMethods.push({
                    id: item.study_type_method,
                    title: item.study_type_method_title,
                    studyTypeTitle: item.study_type_title,
                });

                const bvLikertScales = [];

                item.mapping.forEach((mappingItem) => {
                    mappingItem.displayLabels.forEach((bv) => {
                        const value = likertScaleData[bv.backendVariable];
                        if (value) {
                            bvLikertScales.push(value);
                        }
                    });
                });

                LikertScale[item.study_type_method_title] = [...new Set(bvLikertScales)];
            }
        });

        const filteredStudyTypeMethodOptions = studyTypeMethods.filter((option) => {
            const matchingProducts = productSecondaryData.filter((datum) => datum.studyTypeMethodTitle === option.title);

            // Keep the option if there are no matching products
            if (matchingProducts.length === 0) return true;

            // Remove only if ALL matching products are hidden
            const allHidden = matchingProducts.every((datum) => {
                return productColumnVisibility[`checked--${datum.title.split(" ").join("_")}__${datum.coreDataId}`] || false;
            });

            return !allHidden;
        });

        const filteredCountryOptions = countries.filter((option) => {
            const matchingProducts = lookupSelectedProducts.filter((datum) => datum.country === option);

            // Keep the option if there are no matching products
            if (matchingProducts.length === 0) return true;

            // Remove only if ALL matching products are hidden
            const allHidden = matchingProducts.every((datum) => {
                return productColumnVisibility[`checked--${datum.title.split(" ").join("_")}__${datum.core_data_model}`] || false;
            });

            return !allHidden;
        });

        const arrays = Object.values(LikertScale);
        const allValues = [...new Set(arrays.flat())];
        const hasDifferentValues = allValues.length > 1;
        setDisableInterpolationToggle(!hasDifferentValues);
        if (!hasDifferentValues) {
            dispatch(loadInterpolationToggleState(false));
            dispatch(loadInterpolationDetailsState({}));
        }

        setLikertScaleOptions(allValues);
        setStudyTypeMethodOptions(filteredStudyTypeMethodOptions);
        setCountryOptions(filteredCountryOptions);
    }, [variableMapping, lookupSelectedProducts, likertScaleData, productSecondaryData, productColumnVisibility]);

    useEffect(() => {
        if (likertScaleOptions.length === 1) {
            setSelectedLikertScale(likertScaleOptions[0]);
        } else {
            setSelectedLikertScale("");
        }
    }, [likertScaleOptions, interpolationToggle]);

    useEffect(() => {
        if (studyTypeMethodOptions.length === 1) {
            setSelectedStudyTypeMethod(studyTypeMethodOptions[0]?.id);
        } else {
            setSelectedStudyTypeMethod("");
        }
    }, [studyTypeMethodOptions, interpolationToggle]);

    useEffect(() => {
        if (countryOptions.length === 1) {
            setSelectedCountry(countryOptions[0]);
        } else {
            setSelectedCountry("");
        }
    }, [countryOptions, interpolationToggle]);

    const generatePayload = () => {
        const coreIds = [];
        const studyTypes = [];
        const studyTypeMethods = [];

        const pros = lookupSelectedProducts
            .filter((datum) => productColumnVisibility[`checked--${datum.title.split(" ").join("_")}__${datum.core_data_model}`] !== true)
            .map((item) => {
                coreIds.push(item.core_data_model);
                studyTypes.push(item.study_type_id);
                studyTypeMethods.push(item.study_type_method_id);

                const product = item.title
                    .split(" ")
                    .filter((ele) => ele !== "")
                    .join("_");
                const consumerFilter = consumerFilters[variableMapping.find((v) => v.study_type_method === item.study_type_method_id)?.study_type_method_title]?.currentSelectedFilters || {};

                return {
                    core_data_model_id: item.core_data_model,
                    product_code: item.title,
                    consumer_filters: consumerFilterPayload(consumerFilter, product, item.core_data_model),
                };
            });

        return {
            target_country: selectedCountry,
            target_study_type_method: selectedStudyTypeMethod,
            target_scale: selectedLikertScale,
            core_data_model_id: [...new Set(coreIds)].join(","),
            product_category: productCategoryId,
            study_type: [...new Set(studyTypes)].join(","),
            study_type_methods: [...new Set(studyTypeMethods)].join(","),
            products: pros,
        };
    };

    const handleDialogueSubmit = async () => {
        setIsLoading(true);
        try {
            const url = process.env.REACT_APP_SERVER_PATH + `/deodrants-match-net-analysis/interpolation/`;
            let payload = generatePayload();
            await api
                .post(url, payload)
                .then((res) => {
                    if (res.data?.message) {
                        handleError("error", res.data?.message);
                    } else {
                        const updatedResult = res.data.map((item) => ({
                            ...item,
                            weighted: lookupSelectedProducts.find((product) => `${product.title.split(" ").join("_")}__${product.core_data_model}` === `${item.title.split(" ").join("_")}__${item.coreDataId}`)?.weighted,
                            weightToggle: false,
                        }));

                        const buildKey = (title, coreDataId) => `${(title || "").split(" ").join("_")}__${coreDataId}`;
                        const referenceOrder = (productSecondaryData.length ? productSecondaryData : lookupSelectedProducts).map((item) => buildKey(item.title, item.coreDataId ?? item.core_data_model));
                        const orderedResult = [...updatedResult].sort((a, b) => {
                            const aIndex = referenceOrder.indexOf(buildKey(a.title, a.coreDataId));
                            const bIndex = referenceOrder.indexOf(buildKey(b.title, b.coreDataId));
                            return (aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex) - (bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex);
                        });

                        dispatch(loadProductSecondaryDataState(orderedResult));
                        setIsOpen(false);
                        dispatch(loadInterpolationToggleState(true));

                        const studyTypeMethod = studyTypeMethodOptions.find((item) => item.id.toString() === selectedStudyTypeMethod.toString());
                        dispatch(loadInterpolationDetailsState({ likertScale: selectedLikertScale, studyTypeMethod: studyTypeMethod, country: selectedCountry }));
                    }
                    setIsLoading(false);
                })
                .catch((error) => {
                    handleError("error", error);
                    dispatch(loadInterpolationDetailsState({}));
                    setIsLoading(false);
                });
        } catch (error) {
            handleError("error", error);
            dispatch(loadInterpolationDetailsState({}));
            setIsLoading(false);
        }
    };

    // Dialog Content Renderer
    const returnRadioButton = (value, label) => {
        return (
            <FormControlLabel
                key={value}
                sx={{
                    marginRight: "12px",
                    marginLeft: "12px",
                    "& .MuiTypography-root": {
                        fontSize: 12,
                        color: "#969696",
                    },
                    minWidth: "100px",
                    maxWidth: "250px",
                    borderRadius: "5px",
                    // color: selections.toString() === value.toString() ? theme.palette.primary.main : theme.palette.text.primary,
                    // background: selections.toString() === value.toString() ? "linear-gradient(270deg, rgb(0 103 239 / 0%) 0%, rgb(0 56 139 / 9%) 78%)" : "none",
                }}
                value={value}
                control={
                    <Radio
                        sx={{
                            "& .MuiSvgIcon-root": {
                                fontSize: 16,
                                color: "#969696",
                            },
                            "& .MuiSvgIcon-root:nth-of-type(2)": {
                                fontSize: 16,
                                color: "#1976d2",
                            },
                        }}
                    />
                }
                label={label}
            />
        );
    };

    const renderLikerScale = () => {
        return (
            <FormControl sx={{ width: "100%" }}>
                <FormLabel
                    id="demo-row-radio-buttons-group-label"
                    className="input-label-dialog"
                    sx={{ fontWeight: 600, color: "#333", mb: 1, fontSize: "0.9rem" }}
                >
                    Target Likert Scale
                </FormLabel>
                <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    value={selectedLikertScale}
                    onChange={(e) => setSelectedLikertScale(e.target.value)}
                >
                    {likertScaleOptions.map((ele) => {
                        return returnRadioButton(ele, ele);
                    })}
                </RadioGroup>
            </FormControl>
        );
    };

    const renderTargetStudyTypeMethod = () => {
        return (
            <FormControl sx={{ width: "100%" }}>
                <FormLabel
                    id="demo-row-radio-buttons-group-label"
                    className="input-label-dialog"
                    sx={{ fontWeight: 600, color: "#333", mb: 1, fontSize: "0.9rem" }}
                >
                    Target Study Type Method
                </FormLabel>
                <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    value={selectedStudyTypeMethod}
                    onChange={(e) => setSelectedStudyTypeMethod(e.target.value)}
                >
                    {studyTypeMethodOptions.map((ele) => {
                        const textColor = ele.studyTypeTitle === "Home Use Test" ? "#005eef" : "green";
                        const title = (
                            <>
                                <span>{ele.title} </span>
                                <span style={{ color: textColor }}>({ele.studyTypeTitle})</span>
                            </>
                        );

                        return returnRadioButton(ele.id, title);
                    })}
                </RadioGroup>
            </FormControl>
        );
    };

    const renderTargetCountry = () => {
        return (
            <FormControl sx={{ width: "100%" }}>
                <FormLabel
                    id="demo-row-radio-buttons-group-label"
                    className="input-label-dialog"
                    sx={{ fontWeight: 600, color: "#333", mb: 1, fontSize: "0.9rem" }}
                >
                    Target Country
                </FormLabel>
                <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    value={selectedCountry}
                    onChange={(e) => setSelectedCountry(e.target.value)}
                >
                    {countryOptions.map((ele) => {
                        return returnRadioButton(ele, ele);
                    })}
                </RadioGroup>
            </FormControl>
        );
    };

    return (
        <>
            {isLoading && <Loader />}
            <Tooltip
                title={likertScaleOptions.length === 1 && studyTypeMethodOptions.length === 1 && countryOptions.length === 1 ? "Interpolation not possible as Target Likert Scale Target Study type method and Target Country are the same" : ""}
                placement="bottom"
                componentsProps={{
                    tooltip: {
                        sx: {
                            bgcolor: "#F5F5F5",
                            color: "#000",
                            maxWidth: 300,
                            border: "1px solid #F5F5F5",
                            boxShadow: 2,
                        },
                    },
                    arrow: {
                        sx: {
                            color: "#F5F5F5",
                        },
                    },
                    popper: {
                        modifiers: [
                            {
                                name: "offset",
                                options: {
                                    offset: [0, -7],
                                },
                            },
                        ],
                    },
                }}
            >
                <span>
                    <Button
                        size="small"
                        className={disableInterpolationToggle ? "custom-btn me-4 bold-content" : "custom-btn custom-btn-outlined-2 color-blue me-4 bold-content"}
                        disabled={disableInterpolationToggle}
                        variant="outlined"
                    >
                        Interpolation
                        <Switch
                            size="small"
                            disabled={disableInterpolationToggle}
                            checked={interpolationToggle}
                            onChange={handleInterpolationToggleChange}
                        />
                    </Button>
                </span>
            </Tooltip>
            {isOpen && (
                <Dialog
                    open={interpolationToggle}
                    onClose={handleCloseDialog}
                    fullWidth={true}
                    maxWidth="md"
                >
                    <DialogTitle
                        style={{
                            backgroundColor: "rgb(51, 94, 234,0.1)",
                            color: "rgb(51, 94, 234)",
                            fontSize: 15,
                            textAlign: "center",
                            padding: "10px 12px",
                        }}
                        id="alert-dialog-title"
                    >
                        PLEASE SELECT THE OPTION FOR INTERPOLATION
                        <IconButton
                            aria-label="close"
                            sx={{
                                position: "absolute",
                                right: 8,
                                top: 2,
                                color: "rgb(51, 94, 234) !important",
                            }}
                            onClick={handleCloseDialog}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        <div style={{ display: "flex", flexDirection: "column", gap: "24px", height: 400, overflowY: "auto", padding: "16px 10px" }}>
                            {renderLikerScale()}
                            {renderTargetStudyTypeMethod()}
                            {renderTargetCountry()}
                        </div>
                    </DialogContent>
                    <DialogActions
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            paddingBottom: "20px",
                        }}
                    >
                        <Button
                            size="small"
                            className={selectedLikertScale?.length === 0 || selectedStudyTypeMethod?.length === 0 || selectedCountry?.length === 0 ? "custom-btn me-4 bold-content" : "custom-btn custom-btn-outlined color-blue"}
                            variant="outlined"
                            disabled={selectedLikertScale?.length === 0 || selectedStudyTypeMethod?.length === 0 || selectedCountry?.length === 0}
                            onClick={() => handleDialogueSubmit()}
                        >
                            Get Results
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
}

InterpolationDialog.propTypes = {
    variableMapping: PropTypes.array,
};

export default InterpolationDialog;
